/* External dependencies */
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { createHttpLink } from '@apollo/client/core';
import * as AWSCognito from 'amazon-cognito-identity-js';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { v4 as uuidv4 } from 'uuid';

interface AWSCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: number;
}

export let anonymousClient: ApolloClient<NormalizedCacheObject>;
export let authenticatedClient: ApolloClient<NormalizedCacheObject>;
export let cachedCredentials: AWSCredentials | null = null;

export async function setAnonymousClient(): Promise<ApolloClient<NormalizedCacheObject>> {
  if (anonymousClient) {
    return anonymousClient;
  }

  const anonymousClientConfig = {
    url: process.env.GATSBY_APPSYNC_ENDPOINT,
    region: process.env.GATSBY_REGION,
    auth: {
      type: AUTH_TYPE.API_KEY as AUTH_TYPE.API_KEY,
      apiKey: process.env.GATSBY_AVERSPAY_APP_SYNC_API_KEY,
    },
    offlineConfig: {
      keyPrefix: `client-instance-${uuidv4()}`,
    },
  };

  const { url } = anonymousClientConfig;
  const httpLink = createHttpLink({ uri: url });

  const link = ApolloLink.from([
    createAuthLink(anonymousClientConfig),
    createSubscriptionHandshakeLink(anonymousClientConfig, httpLink),
  ]);

  const cache = new InMemoryCache();

  if (typeof window !== 'undefined') {
    await persistCache({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
    });
  }

  return (anonymousClient = new ApolloClient({
    link,
    cache,
  }));
}

export function getSession() {
  return new Promise((resolve, reject) => {
    const poolData = {
      UserPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
      ClientId: process.env.GATSBY_COGNITO_CLIENT_ID,
    };

    const userPool = new AWSCognito.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No current user'));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      if (!session.isValid()) {
        reject(new Error('Session is not valid'));
        return;
      }

      cognitoUser.getUserAttributes((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        console.log(session);

        resolve(session);
      });
    });
  });
}

function getIdToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const poolData = {
      UserPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
      ClientId: process.env.GATSBY_COGNITO_CLIENT_ID,
    };

    const userPool = new AWSCognito.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No current user'));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session.isValid()) {
        reject(new Error('session is not valid'));
        return;
      }

      const idToken = session.getIdToken().getJwtToken();
      resolve(idToken);
    });
  });
}

async function getAWSCredentials(idToken: string): Promise<AWSCredentials> {
  const identityPoolId = process.env.GATSBY_COGNITO_IDENTITY_POOL_ID;
  const region = process.env.GATSBY_REGION;
  const userPoolId = process.env.GATSBY_COGNITO_USER_POOL_ID;

  const logins = {
    [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken,
  };

  const getIdResponse = await fetch(`https://cognito-identity.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityService.GetId',
    },
    body: JSON.stringify({
      IdentityPoolId: identityPoolId,
      Logins: logins,
    }),
  });

  const { IdentityId } = await getIdResponse.json();

  const getCredentialsResponse = await fetch(`https://cognito-identity.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityService.GetCredentialsForIdentity',
    },
    body: JSON.stringify({
      IdentityId: IdentityId,
      Logins: logins,
    }),
  });

  const data = await getCredentialsResponse.json();

  return {
    accessKeyId: data.Credentials.AccessKeyId,
    secretAccessKey: data.Credentials.SecretKey,
    sessionToken: data.Credentials.SessionToken,
    expiration: data.Credentials.Expiration,
  };
}

async function refreshIdToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const poolData = {
      UserPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
      ClientId: process.env.GATSBY_COGNITO_CLIENT_ID,
    };

    const userPool = new AWSCognito.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No current user'));
      return;
    }

    // Cognito автоматически использует refreshToken если idToken истёк
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      const newIdToken = session.getIdToken().getJwtToken();
      resolve(newIdToken);
    });
  });
}

async function getValidCredentials(): Promise<AWSCredentials> {
  console.log('1. Проверяем credentials...');

  const now = Date.now();
  const bufferTime = 5 * 60 * 1000;

  if (cachedCredentials && cachedCredentials.expiration * 1000 > now + bufferTime) {
    console.log('2. Используем кэшированные credentials');
    console.log('   Expiration:', new Date(cachedCredentials.expiration * 1000).toISOString());
    return cachedCredentials;
  }
  console.log('3. Refreshing credentials...');
  try {
    const idToken = await refreshIdToken();
    console.log('4. ID Token получен:', idToken.substring(0, 50) + '...');

    const newCredentials = await getAWSCredentials(idToken);
    console.log(' 5. AWS Credentials получены:', {
      accessKeyId: newCredentials.accessKeyId,
      expiration: new Date(newCredentials.expiration * 1000).toISOString(),
    });
    cachedCredentials = newCredentials;

    return cachedCredentials;
  } catch (error) {
    console.error(' 6. Ошибка получения credentials:', error);
    throw error;
  }
}

export async function setAuthenticatedClient(): Promise<ApolloClient<NormalizedCacheObject>> {
  console.log(' Apollo: Начинаем создание authenticated client');

  if (authenticatedClient) {
    console.log(' Apollo: Возвращаем кэшированный клиент');

    return authenticatedClient;
  }
  console.log(' Apollo: Проверяем env variables');
  console.log('  URL:', process.env.GATSBY_MY_APPSYNC_ENDPOINT);
  console.log('   Region:', process.env.GATSBY_REGION);
  const authenticatedClientConfig = {
    url: process.env.GATSBY_MY_APPSYNC_ENDPOINT,
    region: process.env.GATSBY_REGION,
    auth: {
      type: AUTH_TYPE.AWS_IAM as AUTH_TYPE.AWS_IAM,
      credentials: async () => {
        console.log('Apollo: Запрашиваем credentials для GraphQL запроса...');

        const result = await getValidCredentials();
        console.log('Apollo: Credentials получены для запроса');

        return {
          accessKeyId: result.accessKeyId,
          secretAccessKey: result.secretAccessKey,
          sessionToken: result.sessionToken,
        };
      },
    },
    offlineConfig: {
      keyPrefix: `client-instance-${uuidv4()}`,
    },
  };
  const { url } = authenticatedClientConfig;
  const httpLink = createHttpLink({ uri: url });
  const link = ApolloLink.from([
    createAuthLink(authenticatedClientConfig),
    createSubscriptionHandshakeLink(authenticatedClientConfig, httpLink),
  ]);

  const cache = new InMemoryCache();

  if (typeof window !== 'undefined') {
    await persistCache({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
    });
  }
  console.log(' Apollo: Client создан успешно');

  return (authenticatedClient = new ApolloClient({
    link,
    cache,
  }));
}
